import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  Plus,
  Search,
  Filter,
  ExternalLink,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Trash2,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  X,
  CheckSquare,
  Square,
  Loader2,
  Zap,
  BarChart3,
  AlertTriangle,
  Check,
  Play,
  List,
  Globe,
  FileText,
} from 'lucide-react';
import { fetchProject } from '../api/projects';
import {
  fetchBacklinks,
  createBacklink,
  deleteBacklink,
  checkBacklink,
  batchRefreshMetrics,
  batchDeleteBacklinks,
  batchAddTag,
  batchMarkBacklinkStatus,
  fetchDomainGroups,
} from '../api/backlinks';
import { batchRefreshDFSMetrics } from '../api/dataforseo';
import { getBacklinkPerformance } from '../api/gsc';
import { useBatchProgress } from '../hooks/useBatchProgress';
import { Database, Tag, Download, Copy, Camera } from 'lucide-react';
import Modal from '../components/Modal';
import StatusBadge from '../components/StatusBadge';
import StatusStatsBar from '../components/StatusStatsBar';
import DomainGroupView from '../components/DomainGroupView';
import CheckTimeline from '../components/CheckTimeline';
import HealthPill from '../components/HealthPill';
import RankRefsCell from '../components/RankRefsCell';
import PortfolioRiskStrip from '../components/PortfolioRiskStrip';
import PortfolioInsights from '../components/PortfolioInsights';
import ChangeFlags from '../components/ChangeFlags';
import Lightbox from '../components/ui/Lightbox';
import { formatRelativeDate, formatDateWithTooltip } from '../utils/dateUtils';
import { cn, linkTypeStyles, estimateDFSCost, trustLevelConfig, calculatePBNScore, calculateLinkHealth } from '../lib/utils';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';

// Helper to parse a URL into domain and path parts for smart display
function formatDomain(url) {
  try {
    const u = new URL(url);
    return { domain: u.hostname.replace('www.', ''), path: u.pathname };
  } catch {
    return { domain: url, path: '' };
  }
}

// Status categories for row tinting (health heatmap)
const ERROR_STATUSES = ['404', '5xx', 'removed', 'timeout'];
const WARNING_STATUSES = ['redirected', 'nofollow_changed'];

// Returns additional className tokens for a table row based on backlink status
function getRowStatusClasses(status) {
  if (ERROR_STATUSES.includes(status)) {
    return 'border-l-2 border-l-destructive/50 bg-destructive/5';
  }
  if (WARNING_STATUSES.includes(status)) {
    return 'border-l-2 border-l-warning/50 bg-warning/5';
  }
  return '';
}

// Thumbnail with error fallback – prevents broken "Scree" images
function ScreenshotThumb({ src, onClick, size = 'md' }) {
  const [hasError, setHasError] = useState(false);
  const dim = size === 'sm' ? 'w-8 h-8' : 'w-10 h-10';
  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';

  if (!src || hasError) {
    return (
      <div className={cn(dim, 'rounded-md bg-muted/30 border border-border/30 flex items-center justify-center')}>
        <Camera className={cn(iconSize, 'text-muted-foreground/20')} />
      </div>
    );
  }
  return (
    <button
      onClick={onClick}
      className="inline-block rounded-md overflow-hidden border border-border/50 hover:border-primary/50 hover:shadow-sm transition-all"
    >
      <img
        src={src}
        alt=""
        className={cn(dim, 'object-cover object-top bg-muted')}
        loading="lazy"
        onError={() => setHasError(true)}
      />
    </button>
  );
}

// Link type badge colors - use semantic tokens from utils
const linkTypeBadge = {
  dofollow: { bg: 'bg-success/20', text: 'text-success', label: 'Follow' },
  nofollow: { bg: 'bg-warning/20', text: 'text-warning', label: 'Nofollow' },
  ugc: { bg: 'bg-info/20', text: 'text-info', label: 'UGC' },
  sponsored: { bg: 'bg-primary/20', text: 'text-primary', label: 'Sponsored' },
};

// Multi-select dropdown for table issue filters. Mirrors the Custom Report Builder criteria.
const ISSUE_FILTER_OPTIONS = [
  { key: 'no_anchor',         label: 'No anchor text' },
  { key: 'broken',            label: 'Broken / Removed' },
  { key: 'redirected',        label: 'Redirected' },
  { key: 'nofollow_changed',  label: 'Nofollow changed' },
  { key: 'pbn_suspicious',    label: 'PBN suspicious' },
  { key: 'high_pbn_score',    label: 'High PBN score (≥4)' },
  { key: 'toxic_quality',     label: 'Toxic quality' },
  { key: 'weak_quality',      label: 'Weak quality' },
  { key: 'duplicate_domains', label: 'Duplicate domains' },
];

function IssueFilterDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const toggle = (key) => {
    const next = value.includes(key) ? value.filter(k => k !== key) : [...value, key];
    onChange(next);
  };

  const count = value.length;
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={cn(
          'inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors',
          count > 0
            ? 'bg-warning/15 border-warning/40 text-warning hover:bg-warning/25'
            : 'bg-muted border-border text-foreground hover:bg-muted/80'
        )}
        title="Filter table by computed issues (no anchor, PBN, quality, duplicate domains)"
      >
        <Filter className="h-4 w-4" />
        Issues{count > 0 ? ` (${count})` : ''}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-64 bg-popover border border-border rounded-lg shadow-lg p-2">
          {ISSUE_FILTER_OPTIONS.map(({ key, label }) => (
            <label
              key={key}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={value.includes(key)}
                onChange={() => toggle(key)}
                className="h-4 w-4 rounded border-border text-primary focus:ring-ring"
              />
              <span className="text-sm text-foreground">{label}</span>
            </label>
          ))}
          {count > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mt-1 w-full text-left px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
            >
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ProjectDetail() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Full Check multi-step state: null | 'crawl' | 'moz' | 'dfs' | 'done'
  const [fullCheckStep, setFullCheckStep] = useState(null);
  const fullCheckStepRef = useRef(null);
  fullCheckStepRef.current = fullCheckStep;

  // WebSocket-based batch check progress (survives page refresh)
  // onComplete fires when crawl batch finishes — chains to next step if fullCheck active
  const { checkingProgress, isRunning: isBatchCheckRunning, startBatchCheck, cancelBatch } = useBatchProgress(id, {
    onComplete: useCallback((data) => {
      if (fullCheckStepRef.current === 'crawl') {
        // Crawl done → trigger Moz refresh for all backlinks
        setFullCheckStep('moz');
      }
    }, []),
  });

  // Local progress state for non-WebSocket batch operations (Moz/DFS metrics)
  const [metricsProgress, setMetricsProgress] = useState({ isRunning: false, type: null });

  // Read filters from URL query params (preserves state across navigation)
  // Support both single status (legacy) and multi-select statuses
  const statusParam = searchParams.get('statuses') || searchParams.get('status') || '';
  const issuesParam = searchParams.get('issues') || '';
  const filters = {
    statuses: statusParam ? statusParam.split(',').filter(Boolean) : [],
    issues: issuesParam ? issuesParam.split(',').filter(Boolean) : [],
    search: searchParams.get('search') || '',
    tag: searchParams.get('tag') || '',
    page: parseInt(searchParams.get('page') || '1', 10),
    limit: parseInt(searchParams.get('limit') || '50', 10),
    sort_by: searchParams.get('sort_by') || 'created_at',
    sort_order: searchParams.get('sort_order') || 'desc',
  };

  // Update filters by updating URL search params
  const setFilters = useCallback((newFilters) => {
    const params = new URLSearchParams();
    if (newFilters.statuses && newFilters.statuses.length > 0) {
      params.set('statuses', newFilters.statuses.join(','));
    }
    if (newFilters.issues && newFilters.issues.length > 0) {
      params.set('issues', newFilters.issues.join(','));
    }
    if (newFilters.search) params.set('search', newFilters.search);
    if (newFilters.tag) params.set('tag', newFilters.tag);
    if (newFilters.page && newFilters.page > 1) params.set('page', newFilters.page.toString());
    if (newFilters.limit && newFilters.limit !== 50) params.set('limit', newFilters.limit.toString());
    if (newFilters.sort_by && newFilters.sort_by !== 'created_at') params.set('sort_by', newFilters.sort_by);
    if (newFilters.sort_order && newFilters.sort_order !== 'desc') params.set('sort_order', newFilters.sort_order);
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  // Helper function to handle sort clicks
  const handleSort = (column) => {
    const newSortOrder = filters.sort_by === column && filters.sort_order === 'desc' ? 'asc' : 'desc';
    setFilters({ ...filters, sort_by: column, sort_order: newSortOrder, page: 1 });
  };

  // Helper to render sort indicator
  const SortIndicator = ({ column }) => {
    if (filters.sort_by !== column) {
      return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />;
    }
    return filters.sort_order === 'asc'
      ? <ArrowUp className="h-4 w-4 text-primary" />
      : <ArrowDown className="h-4 w-4 text-primary" />;
  };

  // Check if any filters are active (non-default values)
  const hasActiveFilters = filters.statuses.length > 0 ||
    filters.issues.length > 0 ||
    filters.search !== '' ||
    filters.tag !== '' ||
    filters.limit !== 50 ||
    filters.sort_by !== 'created_at' ||
    filters.sort_order !== 'desc';

  // Clear all filters and reset to default state
  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [newBacklink, setNewBacklink] = useState({
    backlink_url: '',
    target_url: '',
    expected_anchor: '',
  });
  // Lightbox state for screenshot viewer
  const [lightbox, setLightbox] = useState({ isOpen: false, src: '', title: '' });
  const openLightbox = (src, title) => setLightbox({ isOpen: true, src, title });
  const closeLightbox = () => setLightbox({ isOpen: false, src: '', title: '' });

  // Domain view state
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'domain'
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);

  // Ref to track which backlink is being deleted (prevents rapid delete clicks)
  const deletingBacklinkRef = useRef(null);

  // Fetch project
  const { data: projectData, isError: projectError } = useQuery({
    queryKey: ['project', id],
    queryFn: () => fetchProject(id),
  });

  // Fetch backlinks
  const { data: backlinksData, isLoading } = useQuery({
    queryKey: ['backlinks', id, filters],
    queryFn: () => fetchBacklinks({
      project_id: id,
      statuses: filters.statuses.length > 0 ? filters.statuses.join(',') : undefined,
      search: filters.search || undefined,
      tag: filters.tag || undefined,
      page: filters.page,
      limit: filters.limit,
      sort_by: filters.sort_by,
      sort_order: filters.sort_order,
    }),
  });

  // Fetch domain groups (only when domain view is active)
  const { data: domainGroupsData } = useQuery({
    queryKey: ['domain-groups', id],
    queryFn: () => fetchDomainGroups(id),
    enabled: viewMode === 'domain',
  });

  // Fetch GSC backlink-performance correlation (if GSC is linked)
  const { data: gscPerfData } = useQuery({
    queryKey: ['gsc-backlink-perf', id],
    queryFn: () => getBacklinkPerformance(id),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const gscSummary = gscPerfData?.summary || null;

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => createBacklink({ ...data, project_id: id }),
    onSuccess: () => {
      queryClient.invalidateQueries(['backlinks', id]);
      setIsAddModalOpen(false);
      setNewBacklink({ backlink_url: '', target_url: '', expected_anchor: '' });
      toast.success('Backlink added successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to add backlink');
    },
  });

  // Check mutation (single)
  const checkMutation = useMutation({
    mutationFn: checkBacklink,
    onSuccess: () => {
      queryClient.invalidateQueries(['backlinks', id]);
      toast.success('Check completed');
    },
    onError: () => {
      toast.error('Check failed');
    },
  });

  // Batch refresh Moz metrics mutation (uses backend batch endpoint)
  const batchRefreshMutation = useMutation({
    mutationFn: ({ ids, projectId }) => batchRefreshMetrics(ids, projectId),
    onMutate: () => {
      setMetricsProgress({ isRunning: true, type: 'moz-metrics' });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['backlinks', id]);
      setSelectedIds(new Set());
      setMetricsProgress({ isRunning: false, type: null });
      // If full check is running, chain to DFS step
      if (fullCheckStepRef.current === 'moz') {
        setFullCheckStep('dfs');
      } else {
        toast.success(`Refreshed Moz metrics for ${data.updated} backlinks`);
      }
    },
    onError: (error) => {
      setMetricsProgress({ isRunning: false, type: null });
      if (fullCheckStepRef.current) setFullCheckStep(null);
      toast.error(error.response?.data?.message || 'Failed to refresh Moz metrics');
    },
  });

  // Batch refresh DataForSEO metrics mutation
  const batchRefreshDFSMutation = useMutation({
    mutationFn: ({ ids, projectId, onlyStale }) => batchRefreshDFSMetrics(ids, projectId, onlyStale),
    onMutate: () => {
      setMetricsProgress({ isRunning: true, type: 'dfs-metrics' });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['backlinks', id]);
      setSelectedIds(new Set());
      setMetricsProgress({ isRunning: false, type: null });
      // If full check is running, mark complete
      if (fullCheckStepRef.current === 'dfs') {
        setFullCheckStep('done');
        toast.success('Full check complete — all metrics refreshed');
        setTimeout(() => setFullCheckStep(null), 3000);
      } else {
        toast.success(`Refreshed DataForSEO metrics for ${data.updated} backlinks (${data.domains_queried} domains)`);
      }
    },
    onError: (error) => {
      setMetricsProgress({ isRunning: false, type: null });
      if (fullCheckStepRef.current) setFullCheckStep(null);
      toast.error(error.response?.data?.message || 'Failed to refresh DataForSEO metrics');
    },
  });

  // Full Check step chaining: when fullCheckStep changes, trigger next action
  useEffect(() => {
    if (fullCheckStep === 'moz' && backlinksData?.backlinks?.length > 0) {
      const allIds = backlinksData.backlinks.map(b => b.id);
      batchRefreshMutation.mutate({ ids: allIds, projectId: id });
    }
    if (fullCheckStep === 'dfs' && backlinksData?.backlinks?.length > 0) {
      const allIds = backlinksData.backlinks.map(b => b.id);
      batchRefreshDFSMutation.mutate({ ids: allIds, projectId: id, onlyStale: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullCheckStep]);

  // Batch delete mutation
  const batchDeleteMutation = useMutation({
    mutationFn: (ids) => batchDeleteBacklinks(ids),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['backlinks', id]);
      queryClient.invalidateQueries(['backlink-stats', id]);
      setSelectedIds(new Set());
      toast.success(`Deleted ${data.deleted} backlinks`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete backlinks');
    },
  });

  // Batch tag mutation
  const batchTagMutation = useMutation({
    mutationFn: ({ ids, tag }) => batchAddTag(ids, tag),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['backlinks', id]);
      setSelectedIds(new Set());
      setIsTagModalOpen(false);
      setTagInput('');
      toast.success(`Tag "${tagInput}" added to ${data.updated} backlinks`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to add tag');
    },
  });

  // Batch "Mark as Live" mutation — overrides the crawler's status when the
  // user has independently verified the links are alive (typically used to
  // clear false positives where the crawler IP is being blocked by the host).
  const batchMarkLiveMutation = useMutation({
    mutationFn: (ids) => batchMarkBacklinkStatus(ids, 'live', 'Bulk override from selection'),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['backlinks', id]);
      queryClient.invalidateQueries(['backlinks-all-for-issues', id]);
      setSelectedIds(new Set());
      const note = data.skipped_already_at_status
        ? ` (${data.skipped_already_at_status} already live)`
        : '';
      toast.success(`Marked ${data.updated} as live${note}`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to mark as live');
    },
  });

  // Check if batch operation is running
  const isBatchRunning = isBatchCheckRunning || checkingProgress.isRunning || batchRefreshMutation.isPending || batchRefreshDFSMutation.isPending || batchDeleteMutation.isPending || batchTagMutation.isPending || batchMarkLiveMutation.isPending;

  // Delete mutation with optimistic updates
  const deleteMutation = useMutation({
    mutationFn: deleteBacklink,
    // Optimistic update: remove item from cache immediately
    onMutate: async (backlinkId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries(['backlinks', id, filters]);

      // Snapshot the previous value
      const previousBacklinks = queryClient.getQueryData(['backlinks', id, filters]);

      // Optimistically update to remove the backlink
      queryClient.setQueryData(['backlinks', id, filters], (old) => {
        if (!old) return old;
        return {
          ...old,
          backlinks: old.backlinks.filter((bl) => bl.id !== backlinkId),
          pagination: {
            ...old.pagination,
            total: old.pagination.total - 1,
          },
        };
      });

      // Return context with snapshot for rollback
      return { previousBacklinks };
    },
    onSuccess: () => {
      deletingBacklinkRef.current = null;
      toast.success('Backlink deleted');
      // Invalidate to ensure consistency
      queryClient.invalidateQueries(['backlinks', id]);
    },
    onError: (err, backlinkId, context) => {
      deletingBacklinkRef.current = null;
      // Rollback to previous state on error
      if (context?.previousBacklinks) {
        queryClient.setQueryData(['backlinks', id, filters], context.previousBacklinks);
      }
      toast.error('Failed to delete backlink');
    },
  });

  const project = projectData?.project;

  // Issue-based table filters (no_anchor, toxic, weak, pbn_suspicious, high_pbn, duplicate_domains).
  // These can't run on the backend because health/PBN are computed client-side. When ANY issue
  // filter is active we fetch all backlinks for the project once and filter+paginate client-side.
  const issuesActive = filters.issues.length > 0;
  const { data: allBacklinksForFilter } = useQuery({
    queryKey: ['backlinks-all-for-issues', id, filters.statuses.join(','), filters.search, filters.tag],
    queryFn: () => fetchBacklinks({
      project_id: id,
      statuses: filters.statuses.length > 0 ? filters.statuses.join(',') : undefined,
      search: filters.search || undefined,
      tag: filters.tag || undefined,
      page: 1,
      limit: 10000,
      skip_history: true,
    }),
    enabled: !!id && issuesActive,
    staleTime: 30000,
  });

  // Compute filtered + paginated list when issuesActive.
  const filteredView = useMemo(() => {
    if (!issuesActive || !allBacklinksForFilter?.backlinks) return null;
    const all = allBacklinksForFilter.backlinks;

    // Enrich each backlink with computed health + PBN scores so the matchers can use them.
    const enriched = all.map(b => {
      const health = calculateLinkHealth({
        da: b.domain_authority, pa: b.page_authority, spamScore: b.spam_score,
        status: b.status, linkType: b.link_type,
        consecutiveFailures: b.consecutive_failures,
        lastStatusChangeDays: b.last_status_change ? Math.floor((Date.now() - new Date(b.last_status_change).getTime()) / 86400000) : null,
        anchorDiversity: b.anchor_diversity,
        dfsRank: b.dfs_rank, dfsSpamScore: b.dfs_spam_score,
        traffic: b.traffic, referringDomains: b.dfs_referring_domains,
        referringIps: b.dfs_referring_ips, backlinksCount: b.dfs_backlinks_count,
      });
      const pbn = calculatePBNScore({
        da: parseFloat(b.domain_authority) || 0,
        traffic: parseInt(b.traffic) || 0,
        referringDomains: parseInt(b.dfs_referring_domains) || 0,
        referringIps: parseInt(b.dfs_referring_ips) || 0,
        backlinksCount: parseInt(b.dfs_backlinks_count) || 0,
        spamScore: parseFloat(b.spam_score) || 0,
        dfsSpamScore: b.dfs_spam_score != null ? parseFloat(b.dfs_spam_score) : null,
      });
      return { ...b, health, pbn };
    });

    // Domain count for the duplicate_domains filter.
    const domainCount = {};
    enriched.forEach(b => {
      try {
        const d = new URL(b.backlink_url).hostname.replace(/^www\./, '');
        domainCount[d] = (domainCount[d] || 0) + 1;
      } catch { /* skip */ }
    });
    const isDuplicateDomain = (b) => {
      try {
        const d = new URL(b.backlink_url).hostname.replace(/^www\./, '');
        return (domainCount[d] || 0) > 1;
      } catch { return false; }
    };

    const matchers = {
      no_anchor: (b) => !b.anchor_text_found || !String(b.anchor_text_found).trim(),
      broken: (b) => ['404', '5xx', 'removed', 'timeout'].includes(b.status),
      redirected: (b) => b.status === 'redirected',
      nofollow_changed: (b) => b.status === 'nofollow_changed',
      pbn_suspicious: (b) => b.pbn.trustLevel === 'suspicious',
      high_pbn_score: (b) => (b.pbn.pbnScore || 0) >= 4,
      toxic_quality: (b) => b.health.grade === 'toxic',
      weak_quality: (b) => b.health.grade === 'weak',
      duplicate_domains: isDuplicateDomain,
    };

    // OR-combine selected issue filters: a backlink is included if it matches ANY active criterion.
    let filtered = enriched.filter(b => filters.issues.some(f => matchers[f]?.(b)));

    // Honor the table's existing sort settings.
    const dir = filters.sort_order === 'asc' ? 1 : -1;
    const col = filters.sort_by;
    filtered = [...filtered].sort((a, b) => {
      const av = a[col] ?? '';
      const bv = b[col] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });

    // Client-side pagination.
    const total = filtered.length;
    const pages = Math.max(1, Math.ceil(total / filters.limit));
    const startIdx = (filters.page - 1) * filters.limit;
    const pageRows = filtered.slice(startIdx, startIdx + filters.limit);
    return { backlinks: pageRows, pagination: { page: filters.page, pages, total } };
  }, [issuesActive, allBacklinksForFilter, filters.issues, filters.sort_by, filters.sort_order, filters.page, filters.limit]);

  const backlinks = (issuesActive ? filteredView?.backlinks : backlinksData?.backlinks) || [];
  const pagination = (issuesActive ? filteredView?.pagination : backlinksData?.pagination) || { page: 1, pages: 1, total: 0 };

  // Selection helpers
  const allSelected = backlinks.length > 0 && backlinks.every(b => selectedIds.has(b.id));
  const someSelected = selectedIds.size > 0;

  // Helper to get crawl state for a backlink from the queue (O(1) Map lookup)
  const getCrawlState = (backlinkId) => {
    if (!checkingProgress.isRunning || !checkingProgress.queueMap) return null;
    return checkingProgress.queueMap.get(backlinkId) || null;
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(backlinks.map(b => b.id)));
    }
  };

  const toggleSelect = (backlinkId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(backlinkId)) {
      newSelected.delete(backlinkId);
    } else {
      newSelected.add(backlinkId);
    }
    setSelectedIds(newSelected);
  };

  // Get pending backlinks for quick batch actions
  const pendingBacklinks = useMemo(() =>
    backlinks.filter(b => b.status === 'pending'),
    [backlinks]
  );

  // Check if all Last Checked timestamps are identical (dedup column)
  const lastCheckedDedup = useMemo(() => {
    const checked = backlinks.filter(b => b.last_checked).map(b => formatRelativeDate(b.last_checked));
    if (checked.length === 0) return null;
    const allSame = checked.every(t => t === checked[0]);
    return allSame ? checked[0] : null;
  }, [backlinks]);

  // Compute anchor diversity per domain for low-diversity warnings
  const anchorDiversityMap = useMemo(() => {
    const domainAnchors = new Map();
    for (const bl of backlinks) {
      if (!bl.anchor_text_found) continue;
      let domain;
      try {
        domain = new URL(bl.backlink_url).hostname.replace(/^www\./, '');
      } catch {
        domain = bl.backlink_url;
      }
      if (!domainAnchors.has(domain)) {
        domainAnchors.set(domain, { anchors: new Set(), total: 0 });
      }
      const entry = domainAnchors.get(domain);
      entry.total++;
      entry.anchors.add(bl.anchor_text_found.trim().toLowerCase());
    }
    // Map: domain -> { ratio, total, uniqueAnchors, topAnchor }
    const result = new Map();
    for (const [domain, data] of domainAnchors) {
      const ratio = data.total > 0 ? data.anchors.size / data.total : 1;
      result.set(domain, { ratio, total: data.total, uniqueAnchors: data.anchors.size });
    }
    return result;
  }, [backlinks]);

  const handleCreate = (e) => {
    e.preventDefault();
    createMutation.mutate(newBacklink);
  };

  const handleDeleteBacklink = (backlinkId, backlinkUrl) => {
    // Prevent rapid delete clicks using ref (synchronous check)
    if (deletingBacklinkRef.current === backlinkId || deleteMutation.isPending) {
      return;
    }
    if (window.confirm(`Are you sure you want to delete this backlink?\n\n${backlinkUrl}`)) {
      deletingBacklinkRef.current = backlinkId;
      deleteMutation.mutate(backlinkId);
    }
  };

  const handleBatchCheck = () => {
    if (selectedIds.size === 0 || isBatchRunning) return;
    startBatchCheck('selected', Array.from(selectedIds));
    setSelectedIds(new Set());
  };

  const handleBatchRefreshMetrics = () => {
    if (selectedIds.size === 0 || isBatchRunning) return;
    batchRefreshMutation.mutate({ ids: Array.from(selectedIds), projectId: id });
  };

  const handleBatchRefreshDFSMetrics = () => {
    if (selectedIds.size === 0 || isBatchRunning) return;
    batchRefreshDFSMutation.mutate({ ids: Array.from(selectedIds), projectId: id, onlyStale: false });
  };

  // Count unique domains in selection for DFS cost estimation
  const selectedDomainCount = useMemo(() => {
    const selected = backlinks.filter(b => selectedIds.has(b.id));
    const domains = new Set(selected.map(b => {
      try { return new URL(b.backlink_url).hostname.replace(/^www\./, ''); }
      catch { return b.backlink_url; }
    }));
    return domains.size;
  }, [backlinks, selectedIds]);

  const handleBatchDelete = () => {
    if (selectedIds.size === 0 || isBatchRunning) return;
    if (window.confirm(`Are you sure you want to delete ${selectedIds.size} backlinks? This cannot be undone.`)) {
      batchDeleteMutation.mutate(Array.from(selectedIds));
    }
  };

  const handleBatchTag = (e) => {
    e.preventDefault();
    if (!tagInput.trim() || selectedIds.size === 0 || isBatchRunning) return;
    batchTagMutation.mutate({ ids: Array.from(selectedIds), tag: tagInput.trim() });
  };

  const handleExportSelected = (format) => {
    if (selectedIds.size === 0) return;
    const selectedBacklinks = backlinks.filter(b => selectedIds.has(b.id));

    if (format === 'csv') {
      const headers = ['Backlink URL', 'Target URL', 'Status', 'Link Type', 'DA', 'PA', 'Spam Score', 'Anchor Text', 'Tags', 'Last Checked'];
      const rows = selectedBacklinks.map(b => [
        b.backlink_url,
        b.target_url,
        b.status,
        b.link_type || '',
        b.domain_authority || '',
        b.page_authority || '',
        b.spam_score || '',
        b.anchor_text_found || '',
        b.tags || '',
        b.last_checked || '',
      ]);
      const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backlinks-export-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${selectedBacklinks.length} backlinks to CSV`);
    } else if (format === 'json') {
      const json = JSON.stringify(selectedBacklinks, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backlinks-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${selectedBacklinks.length} backlinks to JSON`);
    } else if (format === 'replacement') {
      // Replacement Request CSV for provider outreach
      const headers = ['Provider', 'Email', 'Backlink URL', 'Target URL', 'Anchor', 'Failure Type', 'Date Detected', 'Link Type'];
      const rows = selectedBacklinks.map(b => [
        b.provider_name || 'Unknown',
        b.provider_contact_email || '',
        b.backlink_url,
        b.target_url,
        b.anchor_text_found || '',
        b.status,
        b.last_status_change || b.last_checked || '',
        b.link_type || '',
      ]);
      const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `replacement-request-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${selectedBacklinks.length} backlinks as replacement request`);
    } else if (format === 'disavow') {
      // Google Disavow List format
      const domains = new Set();
      selectedBacklinks.forEach(b => {
        try {
          const hostname = new URL(b.backlink_url).hostname.replace(/^www\./, '');
          domains.add(`domain:${hostname}`);
        } catch { /* skip invalid URLs */ }
      });
      const content = `# Disavow file generated ${new Date().toISOString().split('T')[0]}\n# ${domains.size} domains from ${selectedBacklinks.length} backlinks\n${[...domains].sort().join('\n')}\n`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `disavow-${new Date().toISOString().split('T')[0]}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported disavow list with ${domains.size} domains`);
    }
  };

  const handleCheckAllPending = async () => {
    if (isBatchRunning) return;
    startBatchCheck('pending');
  };

  const handleCheckAll = async () => {
    if (isBatchRunning) return;
    startBatchCheck('all');
  };

  // Full Check: Crawl → Moz → DFS
  const handleFullCheck = async () => {
    if (isBatchRunning) return;
    setFullCheckStep('crawl');
    startBatchCheck('all');
  };

  // DFS Only for all backlinks in project
  const handleDFSOnlyAll = async () => {
    if (isBatchRunning) return;
    batchRefreshDFSMutation.mutate({ ids: [], projectId: id, onlyStale: false });
  };

  const handleCancelBatch = () => {
    cancelBatch();
  };

  // Generate Agency Replacement Report
  const [reportGenerating, setReportGenerating] = useState(false);

  // Custom Report Builder state
  const [customReportOpen, setCustomReportOpen] = useState(false);
  const [customReportGenerating, setCustomReportGenerating] = useState(false);
  const [customReportFilters, setCustomReportFilters] = useState({
    no_anchor: false,
    broken: false,
    redirected: false,
    nofollow_changed: false,
    pbn_suspicious: false,
    high_pbn_score: false,
    toxic_quality: false,
    weak_quality: false,
    duplicate_domains: false,
  });
  const handleGenerateReport = async () => {
    if (reportGenerating) return;
    setReportGenerating(true);
    try {
      // Fetch ALL backlinks for this project (no pagination limit, skip check history to avoid URI-too-long)
      const allData = await fetchBacklinks({ project_id: id, limit: 10000, page: 1, skip_history: true });
      const all = allData?.backlinks || [];
      if (all.length === 0) {
        toast.error('No backlinks to report on');
        setReportGenerating(false);
        return;
      }

      // Compute health + trust for each backlink
      const enriched = all.map(b => {
        const health = calculateLinkHealth({
          da: b.domain_authority, pa: b.page_authority, spamScore: b.spam_score,
          status: b.status, linkType: b.link_type,
          consecutiveFailures: b.consecutive_failures,
          lastStatusChangeDays: b.last_status_change ? Math.floor((Date.now() - new Date(b.last_status_change).getTime()) / 86400000) : null,
          anchorDiversity: b.anchor_diversity,
          dfsRank: b.dfs_rank, dfsSpamScore: b.dfs_spam_score,
          traffic: b.traffic, referringDomains: b.dfs_referring_domains,
          referringIps: b.dfs_referring_ips, backlinksCount: b.dfs_backlinks_count,
        });
        const pbn = calculatePBNScore({
          da: parseFloat(b.domain_authority) || 0,
          traffic: parseInt(b.traffic) || 0,
          referringDomains: parseInt(b.dfs_referring_domains) || 0,
          referringIps: parseInt(b.dfs_referring_ips) || 0,
          backlinksCount: parseInt(b.dfs_backlinks_count) || 0,
          spamScore: parseFloat(b.spam_score) || 0,
          dfsSpamScore: b.dfs_spam_score != null ? parseFloat(b.dfs_spam_score) : null,
        });
        return { ...b, health, pbn };
      });

      // Categorize issues
      const broken = enriched.filter(b => ['404', '5xx', 'removed', 'timeout'].includes(b.status));
      const redirected = enriched.filter(b => b.status === 'redirected');
      const nofollowChanged = enriched.filter(b => b.status === 'nofollow_changed');
      const suspicious = enriched.filter(b => b.pbn.trustLevel === 'suspicious');
      const caution = enriched.filter(b => b.pbn.trustLevel === 'caution');
      const toxic = enriched.filter(b => b.health.grade === 'toxic');
      const weak = enriched.filter(b => b.health.grade === 'weak');

      // Find duplicate domains (multiple backlinks from same domain)
      const domainMap = {};
      enriched.forEach(b => {
        try {
          const domain = new URL(b.backlink_url).hostname.replace(/^www\./, '');
          (domainMap[domain] = domainMap[domain] || []).push(b);
        } catch { /* skip */ }
      });
      const duplicateDomains = Object.entries(domainMap).filter(([, links]) => links.length > 1);

      // Build CSV
      const csvEsc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const lines = [];

      // Header
      lines.push('');
      lines.push(`AGENCY REPLACEMENT REPORT — ${project?.name || 'Project'}`);
      lines.push(`Generated: ${new Date().toLocaleDateString()} | Total Backlinks: ${all.length}`);
      lines.push('');

      // Summary counts
      lines.push('=== SUMMARY ===');
      lines.push(`Broken/Removed: ${broken.length}`);
      lines.push(`Redirected: ${redirected.length}`);
      lines.push(`Nofollow Changed: ${nofollowChanged.length}`);
      lines.push(`Suspicious (PBN-like): ${suspicious.length}`);
      lines.push(`Caution: ${caution.length}`);
      lines.push(`Toxic Quality: ${toxic.length}`);
      lines.push(`Weak Quality: ${weak.length}`);
      lines.push(`Duplicate Domains: ${duplicateDomains.length} domains (${duplicateDomains.reduce((s, [, l]) => s + l.length, 0)} links)`);

      // Grade distribution
      const grades = { strong: 0, moderate: 0, weak: 0, toxic: 0 };
      enriched.forEach(b => { grades[b.health.grade] = (grades[b.health.grade] || 0) + 1; });
      lines.push('');
      lines.push(`Grade Distribution: Strong ${grades.strong} | Moderate ${grades.moderate} | Weak ${grades.weak} | Toxic ${grades.toxic}`);
      lines.push('');

      // Replacement candidates (the main actionable list)
      const needsAction = enriched.filter(b =>
        ['404', '5xx', 'removed', 'timeout'].includes(b.status) ||
        b.pbn.trustLevel === 'suspicious' ||
        b.health.grade === 'toxic' ||
        b.health.grade === 'weak'
      );

      const csvHeaders = [
        'Issue', 'Backlink URL', 'Target URL', 'Status', 'Health Grade', 'Health Score',
        'Trust Level', 'PBN Score', 'PBN Signals', 'DA', 'PA', 'Spam Score',
        'DFS Spam', 'Traffic', 'Referring Domains', 'Anchor Text', 'Link Type', 'Tags',
      ];

      lines.push('=== BACKLINKS NEEDING REPLACEMENT ===');
      lines.push(csvHeaders.map(csvEsc).join(','));

      // Helper to classify issue
      const getIssue = (b) => {
        const issues = [];
        if (['404', '5xx', 'removed', 'timeout'].includes(b.status)) issues.push('Broken/Removed');
        if (b.pbn.trustLevel === 'suspicious') issues.push('PBN Suspicious');
        if (b.health.grade === 'toxic') issues.push('Toxic Quality');
        else if (b.health.grade === 'weak') issues.push('Weak Quality');
        return issues.join(' + ') || 'Review';
      };

      needsAction
        .sort((a, b) => (a.health.score || 0) - (b.health.score || 0)) // worst first
        .forEach(b => {
          lines.push([
            getIssue(b), b.backlink_url, b.target_url, b.status,
            b.health.grade, b.health.score, b.pbn.trustLevel, b.pbn.pbnScore,
            b.pbn.signals.join('; '), b.domain_authority || '', b.page_authority || '',
            b.spam_score || '', b.dfs_spam_score ?? '', b.traffic ?? '',
            b.dfs_referring_domains ?? '', b.anchor_text_found || '', b.link_type || '',
            b.tags || '',
          ].map(csvEsc).join(','));
        });

      // Duplicate domains section
      if (duplicateDomains.length > 0) {
        lines.push('');
        lines.push('=== DUPLICATE DOMAINS ===');
        lines.push(['Domain', 'Count', 'Backlink URLs'].map(csvEsc).join(','));
        duplicateDomains
          .sort((a, b) => b[1].length - a[1].length)
          .forEach(([domain, links]) => {
            lines.push([domain, links.length, links.map(l => l.backlink_url).join(' | ')].map(csvEsc).join(','));
          });
      }

      // Caution links (not urgent but worth monitoring)
      const cautionOnly = caution.filter(b => !needsAction.includes(b));
      if (cautionOnly.length > 0) {
        lines.push('');
        lines.push('=== CAUTION — MONITOR THESE ===');
        lines.push(csvHeaders.map(csvEsc).join(','));
        cautionOnly.forEach(b => {
          lines.push([
            'Caution', b.backlink_url, b.target_url, b.status,
            b.health.grade, b.health.score, b.pbn.trustLevel, b.pbn.pbnScore,
            b.pbn.signals.join('; '), b.domain_authority || '', b.page_authority || '',
            b.spam_score || '', b.dfs_spam_score ?? '', b.traffic ?? '',
            b.dfs_referring_domains ?? '', b.anchor_text_found || '', b.link_type || '',
            b.tags || '',
          ].map(csvEsc).join(','));
        });
      }

      // Download
      const csv = lines.join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(project?.name || 'project').replace(/[^a-z0-9]/gi, '_')}_agency_report_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Report generated: ${needsAction.length} links need replacement, ${duplicateDomains.length} duplicate domains`);
    } catch (err) {
      console.error('Report generation failed:', err);
      toast.error('Failed to generate report');
    } finally {
      setReportGenerating(false);
    }
  };

  // Custom Report Builder — like the agency report, but only the criteria the user picked.
  const handleGenerateCustomReport = async () => {
    if (customReportGenerating) return;

    const activeFilters = Object.entries(customReportFilters).filter(([, v]) => v).map(([k]) => k);
    if (activeFilters.length === 0) {
      toast.error('Pick at least one filter to include');
      return;
    }

    setCustomReportGenerating(true);
    try {
      const allData = await fetchBacklinks({ project_id: id, limit: 10000, page: 1, skip_history: true });
      const all = allData?.backlinks || [];
      if (all.length === 0) {
        toast.error('No backlinks to report on');
        return;
      }

      // Enrich (same as handleGenerateReport)
      const enriched = all.map(b => {
        const health = calculateLinkHealth({
          da: b.domain_authority, pa: b.page_authority, spamScore: b.spam_score,
          status: b.status, linkType: b.link_type,
          consecutiveFailures: b.consecutive_failures,
          lastStatusChangeDays: b.last_status_change ? Math.floor((Date.now() - new Date(b.last_status_change).getTime()) / 86400000) : null,
          anchorDiversity: b.anchor_diversity,
          dfsRank: b.dfs_rank, dfsSpamScore: b.dfs_spam_score,
          traffic: b.traffic, referringDomains: b.dfs_referring_domains,
          referringIps: b.dfs_referring_ips, backlinksCount: b.dfs_backlinks_count,
        });
        const pbn = calculatePBNScore({
          da: parseFloat(b.domain_authority) || 0,
          traffic: parseInt(b.traffic) || 0,
          referringDomains: parseInt(b.dfs_referring_domains) || 0,
          referringIps: parseInt(b.dfs_referring_ips) || 0,
          backlinksCount: parseInt(b.dfs_backlinks_count) || 0,
          spamScore: parseFloat(b.spam_score) || 0,
          dfsSpamScore: b.dfs_spam_score != null ? parseFloat(b.dfs_spam_score) : null,
        });
        return { ...b, health, pbn };
      });

      // Build the duplicate-domain index (used by the duplicate_domains filter)
      const domainCount = {};
      enriched.forEach(b => {
        try {
          const d = new URL(b.backlink_url).hostname.replace(/^www\./, '');
          domainCount[d] = (domainCount[d] || 0) + 1;
        } catch { /* skip */ }
      });
      const isDuplicateDomain = (b) => {
        try {
          const d = new URL(b.backlink_url).hostname.replace(/^www\./, '');
          return (domainCount[d] || 0) > 1;
        } catch { return false; }
      };

      // OR-combine the selected filters: include a link if it matches ANY active criterion.
      const matchers = {
        no_anchor: (b) => !b.anchor_text_found || !String(b.anchor_text_found).trim(),
        broken: (b) => ['404', '5xx', 'removed', 'timeout'].includes(b.status),
        redirected: (b) => b.status === 'redirected',
        nofollow_changed: (b) => b.status === 'nofollow_changed',
        pbn_suspicious: (b) => b.pbn.trustLevel === 'suspicious',
        high_pbn_score: (b) => (b.pbn.pbnScore || 0) >= 4,
        toxic_quality: (b) => b.health.grade === 'toxic',
        weak_quality: (b) => b.health.grade === 'weak',
        duplicate_domains: isDuplicateDomain,
      };
      const filtered = enriched.filter(b => activeFilters.some(f => matchers[f]?.(b)));

      if (filtered.length === 0) {
        toast.error('No backlinks match the selected filters');
        return;
      }

      // Classify why each row was included so the CSV "Issue" column is meaningful.
      const filterLabels = {
        no_anchor: 'No Anchor',
        broken: 'Broken/Removed',
        redirected: 'Redirected',
        nofollow_changed: 'Nofollow Changed',
        pbn_suspicious: 'PBN Suspicious',
        high_pbn_score: 'High PBN Score',
        toxic_quality: 'Toxic Quality',
        weak_quality: 'Weak Quality',
        duplicate_domains: 'Duplicate Domain',
      };
      const issueOf = (b) => activeFilters.filter(f => matchers[f](b)).map(f => filterLabels[f]).join(' + ') || 'Match';

      // Build CSV — same shape as handleGenerateReport, but a single section for the chosen criteria.
      const csvEsc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const lines = [];
      const filterListReadable = activeFilters.map(f => filterLabels[f]).join(', ');

      lines.push('');
      lines.push(`CUSTOM REPORT — ${project?.name || 'Project'}`);
      lines.push(`Generated: ${new Date().toLocaleDateString()} | Total Backlinks: ${all.length} | Matched: ${filtered.length}`);
      lines.push(`Filters: ${filterListReadable}`);
      lines.push('');

      const csvHeaders = [
        'Issue', 'Backlink URL', 'Target URL', 'Status', 'Health Grade', 'Health Score',
        'Trust Level', 'PBN Score', 'PBN Signals', 'DA', 'PA', 'Spam Score',
        'DFS Spam', 'Traffic', 'Referring Domains', 'Anchor Text', 'Link Type', 'Tags',
      ];
      lines.push('=== MATCHED BACKLINKS ===');
      lines.push(csvHeaders.map(csvEsc).join(','));

      filtered
        .sort((a, b) => (a.health.score || 0) - (b.health.score || 0)) // worst-first
        .forEach(b => {
          lines.push([
            issueOf(b), b.backlink_url, b.target_url, b.status,
            b.health.grade, b.health.score, b.pbn.trustLevel, b.pbn.pbnScore,
            (b.pbn.signals || []).join('; '), b.domain_authority || '', b.page_authority || '',
            b.spam_score || '', b.dfs_spam_score ?? '', b.traffic ?? '',
            b.dfs_referring_domains ?? '', b.anchor_text_found || '', b.link_type || '',
            b.tags || '',
          ].map(csvEsc).join(','));
        });

      // Bonus section: if "duplicate_domains" was picked, append the domain → URLs roll-up.
      if (customReportFilters.duplicate_domains) {
        const duplicateDomains = Object.entries(domainCount)
          .filter(([, c]) => c > 1)
          .sort((a, b) => b[1] - a[1]);
        if (duplicateDomains.length > 0) {
          lines.push('');
          lines.push('=== DUPLICATE DOMAINS ROLL-UP ===');
          lines.push(['Domain', 'Count', 'Backlink URLs'].map(csvEsc).join(','));
          duplicateDomains.forEach(([domain, count]) => {
            const urls = filtered.filter(b => {
              try { return new URL(b.backlink_url).hostname.replace(/^www\./, '') === domain; }
              catch { return false; }
            }).map(b => b.backlink_url).join(' | ');
            lines.push([domain, count, urls].map(csvEsc).join(','));
          });
        }
      }

      // Download
      const csv = lines.join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const filterSlug = activeFilters.length === 1 ? activeFilters[0] : `${activeFilters.length}filters`;
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(project?.name || 'project').replace(/[^a-z0-9]/gi, '_')}_custom_${filterSlug}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Custom report: ${filtered.length} backlinks matched`);
      setCustomReportOpen(false);
    } catch (err) {
      console.error('Custom report failed:', err);
      toast.error('Failed to generate custom report');
    } finally {
      setCustomReportGenerating(false);
    }
  };

  // Show error state for deleted/non-existent project
  if (projectError) {
    return (
      <div className="space-y-6">
        <Link
          to="/projects"
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Projects
        </Link>
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-foreground mb-4">Project Not Found</h1>
          <p className="text-muted-foreground mb-6">
            This project may have been deleted or doesn't exist.
          </p>
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors"
          >
            Return to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={project?.name || 'Loading...'}
        description={project?.target_domain}
        breadcrumbs={[
          { label: 'Projects', href: '/projects' },
          { label: project?.name || '...' },
        ]}
        actions={
          <Button
            onClick={() => setIsAddModalOpen(true)}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            Add Backlink
          </Button>
        }
      />

      {/* Status Stats Bar */}
      <StatusStatsBar
        projectId={id}
        selectedStatuses={filters.statuses}
        onStatusClick={(statuses) => setFilters({ ...filters, statuses, page: 1 })}
      />

      {/* Portfolio Risk Strip */}
      <PortfolioRiskStrip projectId={id} />

      {/* Portfolio Insights (collapsible) */}
      <PortfolioInsights projectId={id} />

      {/* GSC Insights Card (only if GSC linked and has data) */}
      {gscSummary && gscSummary.totalClicks > 0 && (
        <div className="glow-card-filled glow-info p-4 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="h-4 w-4 text-info" />
            <h3 className="text-sm font-semibold text-foreground">GSC Search Traffic Insights</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <p className="text-xs text-muted-foreground">Organic Clicks</p>
              <p className="text-lg font-bold text-foreground">{gscSummary.totalClicks.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Impressions</p>
              <p className="text-lg font-bold text-foreground">{gscSummary.totalImpressions.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Indexed Pages</p>
              <p className="text-lg font-bold text-success">{gscSummary.indexed}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Not Indexed</p>
              <p className={`text-lg font-bold ${gscSummary.notIndexed > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                {gscSummary.notIndexed}
              </p>
            </div>
          </div>
          {gscSummary.topPage && (
            <p className="text-xs text-muted-foreground mt-2">
              Top page: <span className="text-foreground">{gscSummary.topPage.url}</span> ({gscSummary.topPage.clicks} clicks)
            </p>
          )}
        </div>
      )}

      {/* Quick Actions Bar — no backdrop-blur here so dropdown z-index can escape */}
      <div className="flex flex-wrap gap-3 p-4 bg-card/40 border border-border/50 rounded-xl overflow-visible relative z-20">
        <Button
          onClick={handleCheckAllPending}
          disabled={isBatchRunning}
          variant="warning"
          loading={isBatchRunning && checkingProgress.type === 'check' && !fullCheckStep}
          leftIcon={!(isBatchRunning && checkingProgress.type === 'check') && <Play className="h-4 w-4" />}
        >
          Check Pending
        </Button>

        {/* Check All dropdown with Full Check and DFS Only options */}
        <div className="relative z-50 group">
          <Button
            onClick={handleCheckAll}
            disabled={pagination.total === 0 || isBatchRunning}
            variant="secondary"
            leftIcon={<RefreshCw className={cn('h-4 w-4', isBatchRunning && 'animate-spin')} />}
          >
            Check All ({pagination.total}) ▾
          </Button>
          <div className="absolute top-full left-0 mt-1 py-1 bg-card border border-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[60] min-w-[260px]">
            <button
              onClick={handleCheckAll}
              disabled={isBatchRunning}
              className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              <div className="font-medium">Crawl Only</div>
              <div className="text-xs text-muted-foreground">Verify link status (free)</div>
            </button>
            <button
              onClick={handleFullCheck}
              disabled={isBatchRunning}
              className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              <div className="font-medium">Full Check — Crawl + Moz + DFS</div>
              <div className="text-xs text-muted-foreground">All metrics refreshed (~$0.06)</div>
            </button>
            <button
              onClick={handleDFSOnlyAll}
              disabled={isBatchRunning}
              className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              <div className="font-medium">DFS Only</div>
              <div className="text-xs text-muted-foreground">Refresh DataForSEO metrics (~$0.06)</div>
            </button>
          </div>
        </div>

        <div className="w-px h-8 bg-border hidden sm:block" />

        <Button
          onClick={handleGenerateReport}
          disabled={pagination.total === 0 || reportGenerating}
          variant="outline"
          loading={reportGenerating}
          leftIcon={!reportGenerating && <FileText className="h-4 w-4" />}
          title="Generate full agency replacement report (CSV) with broken, suspicious, and weak links"
        >
          Report
        </Button>
        <Button
          onClick={() => setCustomReportOpen(true)}
          disabled={pagination.total === 0}
          variant="outline"
          leftIcon={<Filter className="h-4 w-4" />}
          title="Build a focused report with the criteria you pick (e.g. no anchor text, high PBN score, duplicate domains)"
        >
          Custom Report
        </Button>
        <Button
          onClick={async () => {
            try {
              const allData = await fetchBacklinks({ project_id: id, limit: 10000, page: 1, skip_history: true });
              const all = allData?.backlinks || [];
              const issues = all.filter(b =>
                !['live', 'pending'].includes(b.status) || !b.anchor_text_found
              );
              if (issues.length === 0) {
                toast.error('No issues found to export');
                return;
              }
              const csvEsc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
              const headers = ['Backlink URL', 'Target URL', 'Status', 'Anchor Found', 'Expected Anchor', 'DA', 'Spam Score', 'Link Type', 'Tags', 'Last Checked'];
              const rows = issues.map(b => [
                b.backlink_url, b.target_url, b.status, b.anchor_text_found || '', b.expected_anchor || '',
                b.domain_authority || '', b.spam_score || '', b.link_type || '', b.tags || '', b.last_checked || '',
              ]);
              const csv = [headers, ...rows].map(r => r.map(csvEsc).join(',')).join('\n');
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${(project?.name || 'project').replace(/[^a-z0-9]/gi, '_')}_issues_${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
              URL.revokeObjectURL(url);
              toast.success(`Exported ${issues.length} issues to CSV`);
            } catch (err) {
              console.error('Export issues failed:', err);
              toast.error('Failed to export issues');
            }
          }}
          disabled={pagination.total === 0}
          variant="outline"
          leftIcon={<Download className="h-4 w-4" />}
          title="Export backlinks with issues or no anchor text found (CSV)"
        >
          Export Issues
        </Button>
      </div>

      {/* Compact Progress Bar */}
      {(checkingProgress.isRunning || fullCheckStep) && (
        <div className="flex items-center gap-4 p-3 glow-card-filled glow-pulse animate-in slide-in-from-top-2 duration-200">
          {fullCheckStep === 'done' ? (
            <Check className="h-5 w-5 text-success flex-shrink-0" />
          ) : (
            <Loader2 className="h-5 w-5 text-primary animate-spin flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="font-medium text-foreground">
                {fullCheckStep === 'crawl' && `Step 1/3: Crawling backlinks...`}
                {fullCheckStep === 'moz' && `Step 2/3: Refreshing Moz metrics...`}
                {fullCheckStep === 'dfs' && `Step 3/3: Refreshing DFS metrics...`}
                {fullCheckStep === 'done' && `Full check complete`}
                {!fullCheckStep && checkingProgress.isRunning && (
                  checkingProgress.type === 'check' ? 'Checking backlinks...' : 'Refreshing metrics...'
                )}
              </span>
              {checkingProgress.isRunning && checkingProgress.total > 0 && (
                <span className="text-muted-foreground">
                  {checkingProgress.completed + checkingProgress.failed} / {checkingProgress.total}
                  <span className="ml-2 text-xs">
                    ({Math.round(((checkingProgress.completed + checkingProgress.failed) / checkingProgress.total) * 100)}%)
                  </span>
                </span>
              )}
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden flex">
              {fullCheckStep ? (
                <div
                  className={cn(
                    'h-full transition-all duration-500',
                    fullCheckStep === 'done' ? 'bg-success' : 'bg-primary',
                  )}
                  style={{
                    width: fullCheckStep === 'crawl'
                      ? `${checkingProgress.total > 0 ? ((checkingProgress.completed + checkingProgress.failed) / checkingProgress.total) * 33 : 0}%`
                      : fullCheckStep === 'moz' ? '50%'
                      : fullCheckStep === 'dfs' ? '75%'
                      : '100%',
                  }}
                />
              ) : (
                <>
                  <div
                    className="h-full bg-success transition-all duration-300"
                    style={{ width: `${checkingProgress.total > 0 ? (checkingProgress.completed / checkingProgress.total) * 100 : 0}%` }}
                  />
                  <div
                    className="h-full bg-destructive transition-all duration-300"
                    style={{ width: `${checkingProgress.total > 0 ? (checkingProgress.failed / checkingProgress.total) * 100 : 0}%` }}
                  />
                </>
              )}
            </div>
          </div>
          {!fullCheckStep && (
            <Button
              onClick={handleCancelBatch}
              variant="destructive"
              size="sm"
              leftIcon={<X className="h-4 w-4" />}
            >
              Stop
            </Button>
          )}
        </div>
      )}

      {/* Selection Action Bar */}
      {someSelected && !checkingProgress.isRunning && (
        <div className="flex items-center gap-4 p-4 glow-card-filled animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-center gap-2 text-primary">
            <CheckSquare className="h-5 w-5" />
            <span className="font-medium">{selectedIds.size} selected</span>
          </div>

          <div className="flex-1 flex flex-wrap items-center gap-2">
            <Button
              onClick={handleBatchCheck}
              disabled={isBatchRunning}
              size="sm"
              title="Crawl selected backlinks to verify status (live, broken, redirect, etc.)"
              loading={isBatchRunning && checkingProgress.type === 'check'}
              leftIcon={!(isBatchRunning && checkingProgress.type === 'check') && <Zap className="h-4 w-4" />}
            >
              Check
            </Button>

            <Button
              onClick={handleBatchRefreshMetrics}
              disabled={isBatchRunning}
              size="sm"
              title="Refresh Moz metrics (DA, PA, Spam Score) for selected backlinks"
              className="bg-info hover:bg-info/90 text-info-foreground"
              loading={batchRefreshMutation.isPending}
              leftIcon={!batchRefreshMutation.isPending && <BarChart3 className="h-4 w-4" />}
            >
              Moz
            </Button>

            <Button
              onClick={handleBatchRefreshDFSMetrics}
              disabled={isBatchRunning}
              size="sm"
              title={`Refresh DFS metrics for ${selectedIds.size} backlinks (${selectedDomainCount} domains, ~$${estimateDFSCost(selectedDomainCount)})`}
              variant="secondary"
              loading={batchRefreshDFSMutation.isPending}
              leftIcon={!batchRefreshDFSMutation.isPending && <Database className="h-4 w-4" />}
            >
              DFS
            </Button>

            <div className="w-px h-6 bg-border mx-1" />

            <Button
              onClick={() => setIsTagModalOpen(true)}
              disabled={isBatchRunning}
              size="sm"
              variant="outline"
              leftIcon={<Tag className="h-4 w-4" />}
            >
              Tag
            </Button>

            <Button
              onClick={() => {
                if (selectedIds.size === 0 || isBatchRunning) return;
                if (window.confirm(
                  `Mark ${selectedIds.size} backlink${selectedIds.size === 1 ? '' : 's'} as LIVE?\n\n` +
                  `Use this when you've independently verified the links are alive but ` +
                  `the crawler is reporting them as broken/timeout/removed (commonly because ` +
                  `the host site is blocking our crawler IP). This override is logged to ` +
                  `each backlink's check history with strategy_used='manual_override'.`
                )) {
                  batchMarkLiveMutation.mutate(Array.from(selectedIds));
                }
              }}
              disabled={isBatchRunning}
              size="sm"
              variant="outline"
              loading={batchMarkLiveMutation.isPending}
              leftIcon={!batchMarkLiveMutation.isPending && <Check className="h-4 w-4" />}
              title="Bulk-override status to 'live' for selected backlinks (use after independent verification)"
            >
              Mark Live
            </Button>

            <div className="relative group">
              <Button
                size="sm"
                variant="outline"
                leftIcon={<Download className="h-4 w-4" />}
              >
                Export
              </Button>
              <div className="absolute top-full left-0 mt-1 py-1 bg-card border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[180px]">
                <button
                  onClick={() => handleExportSelected('csv')}
                  className="w-full px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted transition-colors"
                >
                  Export CSV
                </button>
                <button
                  onClick={() => handleExportSelected('json')}
                  className="w-full px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted transition-colors"
                >
                  Export JSON
                </button>
                <div className="border-t border-border my-1" />
                <button
                  onClick={() => handleExportSelected('replacement')}
                  className="w-full px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted transition-colors"
                >
                  <div className="font-medium">Replacement Request</div>
                  <div className="text-xs text-muted-foreground">CSV for provider outreach</div>
                </button>
                <button
                  onClick={() => handleExportSelected('disavow')}
                  className="w-full px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted transition-colors"
                >
                  <div className="font-medium">Disavow List</div>
                  <div className="text-xs text-muted-foreground">Google format (.txt)</div>
                </button>
              </div>
            </div>

            <div className="w-px h-6 bg-border mx-1" />

            <Button
              onClick={handleBatchDelete}
              disabled={isBatchRunning}
              size="sm"
              variant="destructive"
              loading={batchDeleteMutation.isPending}
              leftIcon={!batchDeleteMutation.isPending && <Trash2 className="h-4 w-4" />}
            >
              Delete
            </Button>
          </div>

          <button
            onClick={() => setSelectedIds(new Set())}
            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            placeholder="Search backlinks..."
            className="w-full pl-10 pr-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
          />
        </div>
        <input
          type="text"
          value={filters.tag}
          onChange={(e) => setFilters({ ...filters, tag: e.target.value, page: 1 })}
          placeholder="Filter by tag..."
          className="px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors w-40"
        />

        {/* Duplicates filter */}
        <Button
          onClick={() => {
            if (showDuplicatesOnly) {
              setShowDuplicatesOnly(false);
            } else {
              setViewMode('domain');
              setShowDuplicatesOnly(true);
            }
          }}
          variant={showDuplicatesOnly ? 'warning' : 'outline'}
          size="sm"
          leftIcon={<Copy className="h-4 w-4" />}
        >
          Duplicates{domainGroupsData?.duplicateDomains > 0 ? ` (${domainGroupsData.duplicateDomains})` : ''}
        </Button>

        {/* Issue-based filter dropdown — same criteria as the Custom Report Builder */}
        <IssueFilterDropdown
          value={filters.issues}
          onChange={(next) => setFilters({ ...filters, issues: next, page: 1 })}
        />

        {/* View toggle */}
        <div className="flex items-center border border-border rounded-lg p-1">
          <button
            onClick={() => {
              setViewMode('list');
              setShowDuplicatesOnly(false);
            }}
            className={cn(
              'p-1.5 rounded transition-colors',
              viewMode === 'list' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'
            )}
            title="List view"
          >
            <List className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('domain')}
            className={cn(
              'p-1.5 rounded transition-colors',
              viewMode === 'domain' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'
            )}
            title="Domain view"
          >
            <Globe className="h-4 w-4" />
          </button>
        </div>

        {hasActiveFilters && (
          <Button
            onClick={clearFilters}
            variant="outline"
            leftIcon={<X className="h-4 w-4" />}
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* Active issue filter chips (shown when one or more issue filters are active) */}
      {filters.issues.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Showing only:</span>
          {filters.issues.map(key => {
            const labels = {
              no_anchor: 'No anchor', broken: 'Broken/Removed', redirected: 'Redirected',
              nofollow_changed: 'Nofollow changed', pbn_suspicious: 'PBN suspicious',
              high_pbn_score: 'High PBN score', toxic_quality: 'Toxic', weak_quality: 'Weak',
              duplicate_domains: 'Duplicate domains',
            };
            return (
              <button
                key={key}
                onClick={() => setFilters({ ...filters, issues: filters.issues.filter(k => k !== key), page: 1 })}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-warning/15 border border-warning/30 text-warning text-xs hover:bg-warning/25 transition-colors"
                title={`Remove ${labels[key]} filter`}
              >
                {labels[key]} <X className="h-3 w-3" />
              </button>
            );
          })}
        </div>
      )}

      {/* Domain Group View */}
      {viewMode === 'domain' && (
        <div className="glow-card-filled p-4">
          <DomainGroupView
            groups={domainGroupsData?.groups || []}
            projectId={id}
            onOpenLightbox={openLightbox}
            showDuplicatesOnly={showDuplicatesOnly}
          />
        </div>
      )}

      {/* Backlinks Table */}
      {viewMode === 'list' && (
        <div className="glow-card-filled overflow-hidden p-0">
        <div className="overflow-auto max-h-[calc(100vh-320px)]">
          <table className="w-full">
            <thead className="bg-muted/80 backdrop-blur-sm sticky top-0 z-10">
              <tr>
                {/* 1. Checkbox */}
                <th className="w-12 px-3 py-2">
                  <button
                    onClick={toggleSelectAll}
                    className="p-1 text-muted-foreground hover:text-foreground"
                  >
                    {allSelected ? (
                      <CheckSquare className="h-5 w-5 text-primary" />
                    ) : (
                      <Square className="h-5 w-5" />
                    )}
                  </button>
                </th>
                {/* 2. URL */}
                <th
                  className="text-left px-3 py-2 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none transition-colors"
                  onClick={() => handleSort('backlink_url')}
                >
                  <div className="flex items-center gap-1">
                    Backlink URL
                    <SortIndicator column="backlink_url" />
                  </div>
                </th>
                {/* 3. Screenshot */}
                <th className="w-12 px-2 py-2 text-center text-sm font-medium text-muted-foreground hidden lg:table-cell">
                  <Camera className="h-4 w-4 mx-auto" />
                </th>
                {/* 4. Status */}
                <th
                  className="text-left px-3 py-2 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none transition-colors"
                  onClick={() => handleSort('status')}
                  title="Link status: Live (200 OK), Broken (404/5xx), Redirect, Nofollow, Pending (not yet checked)"
                >
                  <div className="flex items-center gap-1">
                    Status
                    <SortIndicator column="status" />
                  </div>
                </th>
                {/* 5. Health (quality grade pill) */}
                <th
                  className="text-center px-2 py-2 text-sm font-medium text-muted-foreground"
                  title="Link Health: A quality grade (Strong / Moderate / Weak / Toxic) based on DA, spam score, DFS rank, referring domains, traffic, and stability. Click a backlink for full breakdown."
                >
                  Health
                </th>
                {/* 6. Trust (PBN classification) */}
                <th
                  className="text-center px-2 py-2 text-sm font-medium text-muted-foreground hidden md:table-cell"
                  title="Trust classification based on PBN signals: DA/traffic mismatch, link profile inflation, IP diversity, spam score. Suspicious links may be PBN."
                >
                  Trust
                </th>
                {/* 7. DFS Rank · Refs (DFS rank + referring domains) */}
                <th
                  className="text-left px-3 py-2 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none transition-colors hidden lg:table-cell"
                  onClick={() => handleSort('domain_authority')}
                  title="DFS Rank = DataForSEO Domain Rank (lower = better, top sites rank under 10K). Refs = unique referring domains linking to this source domain. Refresh via selecting rows → DFS button."
                >
                  <div className="flex items-center gap-1">
                    DFS Rank · Refs
                    <SortIndicator column="domain_authority" />
                  </div>
                </th>
                {/* 7b. Traffic */}
                <th
                  className="text-right px-3 py-2 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none transition-colors hidden lg:table-cell"
                  onClick={() => handleSort('traffic')}
                  title="Estimated monthly organic search traffic to this domain (from DataForSEO summary/live endpoint). Refresh via DFS check."
                >
                  <div className="flex items-center gap-1 justify-end">
                    Traffic
                    <SortIndicator column="traffic" />
                  </div>
                </th>
                {/* 7. Link Type */}
                <th
                  className="text-left px-3 py-2 text-sm font-medium text-muted-foreground hidden lg:table-cell"
                  title="Link type: dofollow passes SEO value, nofollow does not. Detected from the actual link HTML."
                >
                  Link Type
                </th>
                {/* 8. Anchor */}
                <th
                  className="text-left px-3 py-2 text-sm font-medium text-muted-foreground hidden xl:table-cell"
                  title="Anchor text: the clickable text of the backlink as found on the page"
                >
                  Anchor
                </th>
                {/* 8b. Provider */}
                <th className="text-left px-2 py-2 text-sm font-medium text-muted-foreground hidden xl:table-cell" title="Link provider/agency">
                  Provider
                </th>
                {/* 9. Checked (CheckTimeline) */}
                <th
                  className="text-left px-3 py-2 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground select-none transition-colors"
                  onClick={() => handleSort('last_checked')}
                  title="When this backlink was last crawled and verified. Shows relative time and a mini timeline of recent checks."
                >
                  <div className="flex flex-col">
                    <div className="flex items-center gap-1">
                      Checked
                      <SortIndicator column="last_checked" />
                    </div>
                    {lastCheckedDedup && (
                      <span className="text-[10px] font-normal text-muted-foreground/60">{lastCheckedDedup}</span>
                    )}
                  </div>
                </th>
                {/* 10. Actions */}
                <th className="text-right px-3 py-2 text-sm font-medium text-muted-foreground">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={12} className="px-3 py-2.5">
                      <div className="h-6 bg-muted rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : backlinks.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-3 py-12 text-center text-muted-foreground">
                    No backlinks found. Add your first backlink to get started.
                  </td>
                </tr>
              ) : (
                backlinks.map((backlink, index) => {
                  const crawlState = getCrawlState(backlink.id);
                  return (
                  <tr
                    key={backlink.id}
                    className={cn(
                      'hover:bg-muted/50 transition-colors',
                      // Zebra striping for odd rows
                      index % 2 === 1 && 'bg-muted/20',
                      // Status-based row tinting (health heatmap)
                      getRowStatusClasses(backlink.status),
                      // Selected row highlight takes precedence
                      selectedIds.has(backlink.id) && 'bg-primary/10',
                      crawlState === 'processing' && 'bg-primary/5 animate-pulse'
                    )}
                  >
                    <td className="px-3 py-2.5">
                      <button
                        onClick={() => toggleSelect(backlink.id)}
                        className="p-1 text-muted-foreground hover:text-foreground"
                      >
                        {selectedIds.has(backlink.id) ? (
                          <CheckSquare className="h-5 w-5 text-primary" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {/* Inline crawl state indicator */}
                        {crawlState === 'processing' && (
                          <div className="flex-shrink-0">
                            <Loader2 className="h-4 w-4 text-primary animate-spin" />
                          </div>
                        )}
                        {crawlState === 'waiting' && (
                          <div className="flex-shrink-0">
                            <div className="h-3 w-3 rounded-full bg-muted-foreground/30 animate-pulse" />
                          </div>
                        )}
                        {crawlState === 'done' && (
                          <div className="flex-shrink-0">
                            <Check className="h-4 w-4 text-success" />
                          </div>
                        )}
                        {crawlState === 'error' && (
                          <div className="flex-shrink-0">
                            <X className="h-4 w-4 text-destructive" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          {(() => {
                            const parsed = formatDomain(backlink.backlink_url);
                            const targetParsed = formatDomain(backlink.target_url);
                            return (
                              <>
                                <Link
                                  to={`/backlinks/${backlink.id}`}
                                  state={{ filters }}
                                  className="group/url block"
                                  title={backlink.backlink_url}
                                >
                                  <span className="text-foreground font-medium hover:text-primary transition-colors">
                                    {parsed.domain}
                                    <ChangeFlags backlink={backlink} />
                                  </span>
                                  {parsed.path && parsed.path !== '/' && (
                                    <span className="text-xs text-muted-foreground block truncate max-w-[220px]">
                                      {parsed.path}
                                    </span>
                                  )}
                                </Link>
                                <span
                                  className="inline-flex items-center gap-1 mt-0.5 text-xs text-muted-foreground/70"
                                  title={backlink.target_url}
                                >
                                  <span className="text-muted-foreground/50">&rarr;</span>
                                  <span className="px-1.5 py-0.5 bg-muted/50 rounded text-[11px] truncate max-w-[140px]">
                                    {targetParsed.domain}
                                  </span>
                                </span>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-2.5 text-center hidden lg:table-cell">
                      <ScreenshotThumb
                        src={backlink.screenshot_url}
                        onClick={() => openLightbox(backlink.screenshot_url, backlink.backlink_url)}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      {crawlState === 'processing' ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-primary/20 text-primary">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Checking...
                        </span>
                      ) : crawlState === 'waiting' ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                          <div className="h-2 w-2 rounded-full bg-muted-foreground animate-pulse" />
                          Queued
                        </span>
                      ) : (
                        <StatusBadge status={backlink.status} />
                      )}
                    </td>
                    {/* 5. Health (quality grade pill) */}
                    <td className="px-2 py-2.5 text-center">
                      <HealthPill
                        qualityScore={backlink.quality_score}
                        da={backlink.domain_authority}
                        pa={backlink.page_authority}
                        spamScore={backlink.spam_score}
                        traffic={backlink.traffic}
                        referringDomains={backlink.dfs_referring_domains}
                        referringIps={backlink.dfs_referring_ips}
                        backlinksCount={backlink.dfs_backlinks_count}
                        dfsSpamScore={backlink.dfs_spam_score}
                        dfsRank={backlink.dfs_rank}
                        dfsBacklinksCount={backlink.dfs_backlinks_count}
                        dfsReferringDomains={backlink.dfs_referring_domains}
                        consecutiveFailures={backlink.consecutive_failures}
                        lastStatusChange={backlink.last_status_change}
                        lastResponseMs={backlink.last_response_ms}
                      />
                    </td>
                    {/* 6. Trust */}
                    <td className="px-2 py-2.5 text-center hidden md:table-cell">
                      {(() => {
                        // Use stored pbn_score/trust_level if available, otherwise compute client-side
                        let tl = backlink.trust_level;
                        let pbn = backlink.pbn_score;
                        let sigs = [];
                        if (!tl) {
                          const computed = calculatePBNScore({
                            da: parseFloat(backlink.domain_authority) || 0,
                            traffic: parseInt(backlink.traffic) || 0,
                            referringDomains: parseInt(backlink.dfs_referring_domains) || 0,
                            referringIps: parseInt(backlink.dfs_referring_ips) || 0,
                            backlinksCount: parseInt(backlink.dfs_backlinks_count) || 0,
                            spamScore: parseFloat(backlink.spam_score) || 0,
                            dfsSpamScore: backlink.dfs_spam_score != null ? parseFloat(backlink.dfs_spam_score) : null,
                          });
                          tl = computed.trustLevel;
                          pbn = computed.pbnScore;
                          sigs = computed.signals;
                        }
                        const cfg = trustLevelConfig[tl] || trustLevelConfig.unknown;
                        return (
                          <span
                            className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap', cfg.bg, cfg.color)}
                            title={`PBN Score: ${pbn ?? '?'}/5${sigs.length > 0 ? '\n' + sigs.join(', ') : ''}`}
                          >
                            {cfg.label}
                          </span>
                        );
                      })()}
                    </td>
                    {/* 7. DFS Rank · Refs */}
                    <td className="px-3 py-2.5 hidden lg:table-cell">
                      <RankRefsCell
                        dfsRank={backlink.dfs_rank}
                        referringDomains={backlink.dfs_referring_domains}
                        da={backlink.domain_authority}
                        pa={backlink.page_authority}
                        spamScore={backlink.spam_score}
                        traffic={backlink.traffic}
                        backlinksCount={backlink.dfs_backlinks_count}
                        dfsSpamScore={backlink.dfs_spam_score}
                      />
                    </td>
                    {/* 7b. Traffic */}
                    <td className="px-3 py-2.5 text-right hidden lg:table-cell">
                      {backlink.traffic != null && backlink.traffic > 0 ? (
                        <span className="text-sm tabular-nums font-medium text-blue-400" title={`${backlink.traffic.toLocaleString()} estimated organic visits/month`}>
                          {backlink.traffic >= 1000000
                            ? `${(backlink.traffic / 1000000).toFixed(1)}M`
                            : backlink.traffic >= 1000
                              ? `${(backlink.traffic / 1000).toFixed(1)}K`
                              : backlink.traffic.toLocaleString()
                          }<span className="text-muted-foreground text-xs font-normal">/mo</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40 text-xs">&mdash;</span>
                      )}
                    </td>
                    {/* 7. Link Type */}
                    <td className="px-3 py-2.5 hidden lg:table-cell">
                      {backlink.link_type ? (
                        <span className={cn(
                          'px-2 py-0.5 rounded text-xs font-medium',
                          linkTypeBadge[backlink.link_type]?.bg || 'bg-secondary',
                          linkTypeBadge[backlink.link_type]?.text || 'text-secondary-foreground'
                        )}>
                          {linkTypeBadge[backlink.link_type]?.label || backlink.link_type}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    {/* 8. Anchor (with diversity warning) */}
                    <td className="px-3 py-2.5 hidden xl:table-cell">
                      {backlink.anchor_text_found ? (
                        <div className="group/anchor flex items-center gap-1 max-w-[180px]">
                          <span className="text-success text-sm truncate" title={backlink.anchor_text_found}>
                            {backlink.anchor_text_found.length > 30
                              ? backlink.anchor_text_found.substring(0, 30) + '...'
                              : backlink.anchor_text_found}
                          </span>
                          {(() => {
                            let domain;
                            try {
                              domain = new URL(backlink.backlink_url).hostname.replace(/^www\./, '');
                            } catch { domain = null; }
                            const diversity = domain ? anchorDiversityMap.get(domain) : null;
                            if (diversity && diversity.ratio <= 0.2 && diversity.total >= 3) {
                              return (
                                <AlertTriangle
                                  className="h-3.5 w-3.5 text-warning flex-shrink-0"
                                  title={`Low anchor diversity: ${diversity.total} links with only ${diversity.uniqueAnchors} unique anchor${diversity.uniqueAnchors === 1 ? '' : 's'}`}
                                />
                              );
                            }
                            return null;
                          })()}
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(backlink.anchor_text_found);
                              toast.success('Anchor text copied');
                            }}
                            className="flex-shrink-0 opacity-0 group-hover/anchor:opacity-100 p-0.5 text-muted-foreground hover:text-foreground transition-all"
                            title="Copy anchor text"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                      ) : backlink.status === 'live' ? (
                        <span className="text-warning text-sm">No anchor</span>
                      ) : backlink.status === 'pending' ? (
                        <span className="text-muted-foreground text-sm">&mdash;</span>
                      ) : (
                        <span className="text-destructive text-sm">Not found</span>
                      )}
                    </td>
                    {/* Provider badge */}
                    <td className="px-2 py-2.5 hidden xl:table-cell">
                      {backlink.provider_id ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary truncate max-w-[100px]" title={backlink.provider_name || ''}>
                          {backlink.provider_name || 'Provider'}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <CheckTimeline
                        recentChecks={backlink.recent_checks || []}
                        lastChecked={backlink.last_checked}
                      />
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => checkMutation.mutate(backlink.id)}
                          disabled={checkMutation.isPending || crawlState === 'processing' || crawlState === 'waiting'}
                          className={cn(
                            "relative group/action p-1.5 rounded-lg transition-all duration-200",
                            crawlState === 'processing'
                              ? "text-primary bg-primary/10"
                              : "text-muted-foreground hover:text-primary hover:bg-primary/10"
                          )}
                        >
                          <RefreshCw className={cn(
                            'h-4 w-4',
                            (checkMutation.isPending || crawlState === 'processing') && 'animate-spin'
                          )} />
                          <span className="absolute -top-7 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-foreground text-background opacity-0 group-hover/action:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            {crawlState === 'processing' ? 'Checking...' : 'Check'}
                          </span>
                        </button>
                        <a
                          href={backlink.backlink_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="relative group/action p-1.5 text-muted-foreground hover:text-info hover:bg-info/10 rounded-lg transition-all duration-200"
                        >
                          <ExternalLink className="h-4 w-4" />
                          <span className="absolute -top-7 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-foreground text-background opacity-0 group-hover/action:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Open
                          </span>
                        </a>
                        <button
                          onClick={() => handleDeleteBacklink(backlink.id, backlink.backlink_url)}
                          disabled={deleteMutation.isPending && deletingBacklinkRef.current === backlink.id}
                          className="relative group/action p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-all duration-200"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="absolute -top-7 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-foreground text-background opacity-0 group-hover/action:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                            Delete
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );})
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <p className="text-sm text-muted-foreground tabular-nums">
              Showing{' '}
              <span className="font-medium text-foreground">
                {pagination.total === 0 ? 0 : ((pagination.page - 1) * filters.limit) + 1}
              </span>
              {' '}to{' '}
              <span className="font-medium text-foreground">
                {Math.min(pagination.page * filters.limit, pagination.total)}
              </span>
              {' '}of{' '}
              <span className="font-medium text-foreground">{pagination.total}</span>
            </p>
            <div className="flex items-center gap-1.5">
              <label htmlFor="rows-per-page" className="text-xs text-muted-foreground">
                Rows:
              </label>
              <select
                id="rows-per-page"
                value={filters.limit}
                onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value, 10), page: 1 })}
                className="px-2 py-1 text-xs bg-muted border border-border rounded text-foreground focus:ring-1 focus:ring-ring transition-colors"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {pagination.pages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setFilters({ ...filters, page: 1 })}
                disabled={filters.page === 1}
                className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="First page"
              >
                First
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={filters.page === 1}
                className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              {/* Page number buttons */}
              {(() => {
                const pages = [];
                const totalPages = pagination.pages;
                const current = filters.page;
                // Show at most 5 page buttons centered around current page
                let start = Math.max(1, current - 2);
                let end = Math.min(totalPages, start + 4);
                // Adjust start if we are near the end
                if (end - start < 4) {
                  start = Math.max(1, end - 4);
                }
                for (let i = start; i <= end; i++) {
                  pages.push(i);
                }
                return pages.map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setFilters({ ...filters, page: pageNum })}
                    className={cn(
                      'min-w-[32px] h-8 px-2 text-sm rounded transition-colors',
                      pageNum === current
                        ? 'bg-primary text-primary-foreground font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    )}
                  >
                    {pageNum}
                  </button>
                ));
              })()}

              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={filters.page >= pagination.pages}
                className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: pagination.pages })}
                disabled={filters.page >= pagination.pages}
                className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Last page"
              >
                Last
              </button>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Add Backlink Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setNewBacklink({ backlink_url: '', target_url: '', expected_anchor: '' });
        }}
        title="Add Backlink"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Backlink URL *
            </label>
            <input
              type="url"
              value={newBacklink.backlink_url}
              onChange={(e) => setNewBacklink({ ...newBacklink, backlink_url: e.target.value })}
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
              placeholder="https://example.com/page-with-link"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              The page that contains the link to your website
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Target URL *
            </label>
            <input
              type="url"
              value={newBacklink.target_url}
              onChange={(e) => setNewBacklink({ ...newBacklink, target_url: e.target.value })}
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
              placeholder="https://yoursite.com/landing-page"
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              The URL on your website that the backlink points to
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Expected Anchor Text
            </label>
            <input
              type="text"
              value={newBacklink.expected_anchor}
              onChange={(e) => setNewBacklink({ ...newBacklink, expected_anchor: e.target.value })}
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
              placeholder="Click here"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIsAddModalOpen(false);
                setNewBacklink({ backlink_url: '', target_url: '', expected_anchor: '' });
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending}
              loading={createMutation.isPending}
            >
              {createMutation.isPending ? 'Adding...' : 'Add Backlink'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Screenshot Lightbox */}
      <Lightbox
        isOpen={lightbox.isOpen}
        onClose={closeLightbox}
        src={lightbox.src}
        title={lightbox.title}
      />

      {/* Add Tag Modal */}
      <Modal
        isOpen={isTagModalOpen}
        onClose={() => {
          setIsTagModalOpen(false);
          setTagInput('');
        }}
        title="Add Tag to Selected Backlinks"
      >
        <form onSubmit={handleBatchTag} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Tag Name
            </label>
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
              placeholder="e.g., guest-post, sponsored, outreach"
              required
              autoFocus
            />
            <p className="text-xs text-muted-foreground mt-1">
              This tag will be added to {selectedIds.size} selected backlink{selectedIds.size !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIsTagModalOpen(false);
                setTagInput('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={batchTagMutation.isPending || !tagInput.trim()}
              loading={batchTagMutation.isPending}
            >
              Add Tag
            </Button>
          </div>
        </form>
      </Modal>

      {/* Custom Report Modal */}
      <Modal
        isOpen={customReportOpen}
        onClose={() => setCustomReportOpen(false)}
        title="Custom Report Builder"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Pick the issues you want included. The report includes any backlink that matches <strong>any</strong> of the selected criteria.
          </p>

          <div className="space-y-2">
            {[
              { key: 'no_anchor', label: 'No anchor text', desc: 'Backlinks where the anchor text wasn\'t found or is empty' },
              { key: 'broken', label: 'Broken / Removed (404, 5xx, removed, timeout)', desc: 'Links that no longer resolve' },
              { key: 'redirected', label: 'Redirected', desc: 'Links that 301/302 to a different URL' },
              { key: 'nofollow_changed', label: 'Nofollow changed', desc: 'Was dofollow, now nofollow (or vice versa)' },
              { key: 'pbn_suspicious', label: 'PBN suspicious', desc: 'Trust level flagged as suspicious by the PBN heuristics' },
              { key: 'high_pbn_score', label: 'High PBN score (≥4)', desc: 'PBN score 4 or 5 — strong signals of a network site' },
              { key: 'toxic_quality', label: 'Toxic quality', desc: 'Health grade = toxic' },
              { key: 'weak_quality', label: 'Weak quality', desc: 'Health grade = weak' },
              { key: 'duplicate_domains', label: 'Duplicate domains', desc: 'Multiple backlinks from the same root domain' },
            ].map(({ key, label, desc }) => (
              <label
                key={key}
                className="flex items-start gap-3 p-3 bg-muted/40 hover:bg-muted/60 border border-border rounded-lg cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={customReportFilters[key]}
                  onChange={(e) => setCustomReportFilters(f => ({ ...f, [key]: e.target.checked }))}
                  className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-ring"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground">{label}</div>
                  <div className="text-xs text-muted-foreground">{desc}</div>
                </div>
              </label>
            ))}
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-border">
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setCustomReportFilters(Object.fromEntries(Object.keys(customReportFilters).map(k => [k, false])))}
            >
              Clear all
            </button>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setCustomReportOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleGenerateCustomReport}
                loading={customReportGenerating}
                disabled={customReportGenerating || Object.values(customReportFilters).every(v => !v)}
                leftIcon={!customReportGenerating && <Download className="h-4 w-4" />}
              >
                Download Report
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ProjectDetail;
